// TKT-221: canonical 5-tier role / permission model.
//
// The five tiers are a strict hierarchy:
//   inactive(0) < employee(1) < sales(2) < admin(3) < superadmin(4)
// Each higher tier can access everything the lower tiers can, plus more, so
// module access reduces to "does the user's level meet the module's minimum?".
//
// Inactive is represented by the existing is_active=false flag (login is
// already blocked server-side). Subcontractor (TKT-461) is its own user_type
// at the employee tier, but it only gets Crew Mode for the trucks the office
// gives it (see isSubcontractor and the server's subcontractor allowlist).
//
// This works alongside the legacy gates: admin/superadmin resolve exactly as
// before, and legacy user_type 'normal'/'crew' map to the employee tier.

export const ROLE_LEVELS = {
  inactive: 0,
  subcontractor: 1,
  employee: 1,
  crew_manager: 1,
  sales: 2,
  admin: 3,
  superadmin: 4,
};

// Canonical role for a user object ({ user_type, is_active }).
export const getUserRole = (user) => {
  if (!user) return 'inactive';
  if (user.is_active === false) return 'inactive';
  switch (user.user_type) {
    case 'superadmin': return 'superadmin';
    case 'admin': return 'admin';
    case 'sales': return 'sales';
    case 'crew_manager': return 'crew_manager';
    case 'subcontractor': return 'subcontractor';
    case 'employee':
    case 'normal':
    case 'crew':
    default:
      return 'employee';
  }
};

export const getRoleLevel = (user) => ROLE_LEVELS[getUserRole(user)] ?? 0;

// Selectable tiers for the User Management dropdown (Inactive is the is_active
// toggle, not a user_type). Super Admin is offered only by a Super Admin.
export const ASSIGNABLE_ROLES = [
  { value: 'employee', labelKey: 'roles.employee', label: 'Employee' },
  { value: 'crew_manager', labelKey: 'roles.crewManager', label: 'Crew Manager' },
  { value: 'sales', labelKey: 'roles.sales', label: 'Sales' },
  { value: 'admin', labelKey: 'roles.admin', label: 'Admin' },
  { value: 'superadmin', labelKey: 'roles.superadmin', label: 'Super Admin' },
  { value: 'subcontractor', labelKey: 'roles.subcontractor', label: 'Subcontractor' },
];

// TKT-461: subcontractors only use Crew Mode, for their own trucks: no
// dashboard, no vehicle inspection, no personal time clock.
export const isSubcontractor = (user) => user?.user_type === 'subcontractor';

// Module / dashboard-page id -> minimum tier required. Keys match the page ids
// used by Dashboard.js (menuItems + adminItems). Pages not listed here (detail
// drill-downs like customer-detail) inherit access from their entry point and
// are intentionally not hard-gated in Layer A.
export const PAGE_MIN_ROLE = {
  // Employee tier
  dashboard: 'employee',
  tasks: 'employee',
  'crew-mode': 'employee',
  inspections: 'employee',
  documents: 'employee',
  'employee-forms': 'employee',
  calendar: 'employee',
  // Sales tier
  // TKT-287: retest of the TKT-221 rollout found Employee still saw the
  // Calendar/Jobs/Live Tracking views inside Summer/Winter/Construction
  // Logs (those pages render the full JobManagement screen - office-wide
  // calendar, job list, and live GPS tracking - not a personal log).
  // Crew already has their own truck-scoped view via Crew Mode / Today's
  // Schedule, so these office-wide log screens are Sales tier and up.
  'summer-logs': 'sales',
  'winter-logs': 'sales',
  'construction-logs': 'sales',
  quotes: 'sales',
  signatures: 'sales',
  // TKT-573: every sales person can see all quotes, including Pending
  // (approving is still Admin / managers only, checked on the server).
  'pending-quotes': 'sales',
  customers: 'sales',
  'sales-tracker': 'sales',
  tickets: 'sales',
  jobs: 'sales',
  'live-tracking': 'sales',
  // Admin tier
  operations: 'admin',
  'route-setup': 'admin',
  'routes-list': 'admin',
  users: 'admin',
  'fleet-management': 'admin',
  // Oct 2026: the client asked for admin to see every function except
  // budget and pricing, so Reports, Employees, HR and Admin Settings moved
  // down from superadmin (TKT-221 had them superadmin-only).
  reports: 'admin',
  employees: 'admin',
  'hr-management': 'admin',
  'hr-dashboard': 'admin',
  'timesheet-calendar': 'admin',
  'leave-management': 'admin',
  'absence-tracking': 'admin',
  'employee-discipline': 'admin',
  'performance-reviews': 'admin',
  'hr-documents': 'admin',
  'employee-forms-admin': 'admin',
  'admin-settings': 'admin',
  'quote-pdf-list': 'admin',
  'quote-report': 'admin',
  timesheets: 'admin',
  scheduler: 'admin',
  'orphaned-job-sites': 'admin',
  'system-email-settings': 'admin',
  // Super Admin tier: budget and pricing
  budget: 'superadmin',
  'service-pricing': 'superadmin',
  'turf-admin': 'superadmin',
};

const CREW_MANAGER_PAGES = new Set([
  'operations',
  'tasks',
  'crew-mode',
  'inspections',
  'documents',
  'employee-forms',
  'calendar',
  'jobs',
  'live-tracking',
  'fleet-management',
]);

export const canAccessPage = (user, pageId) => {
  if (getUserRole(user) === 'subcontractor') return pageId === 'crew-mode';
  if (getUserRole(user) === 'crew_manager') {
    return CREW_MANAGER_PAGES.has(pageId);
  }
  const min = PAGE_MIN_ROLE[pageId];
  if (!min) return true; // unmapped detail pages inherit from their entry point
  return getRoleLevel(user) >= ROLE_LEVELS[min];
};

export const hasMinRole = (user, role) => getRoleLevel(user) >= (ROLE_LEVELS[role] ?? 99);

// TKT-305: labourers/crew see address only on clock-in and field logs.
// Crew managers and office roles (sales/admin/superadmin) can still see
// customer phone numbers.
const CUSTOMER_PHONE_VIEW_TYPES = new Set([
  'crew_manager',
  'sales',
  'admin',
  'superadmin',
]);

export const canViewCustomerPhone = (user) =>
  CUSTOMER_PHONE_VIEW_TYPES.has(user?.user_type);
